/**
 * Multi-Collection Bands JavaScript - VARIANT MODE
 * Masina Diamonds - Custom Collection Page showing ALL Variants
 * 
 * Features:
 * - Load ALL variants from multiple collections as individual cards
 * - Each variant displayed as its own card
 * - Client-side filtering by ANY variant option (Color, Width, Finish, Style, Shank, etc.)
 * - Price range filtering
 * - Search functionality
 * - Sorting
 * - Responsive mobile drawer
 * - Lazy loading / Load more
 */

(function() {
  'use strict';

  class MultiCollectionBands {
    constructor() {
      this.container = document.querySelector('.multi-collection-bands');
      if (!this.container) return;

      this.dataElement = document.getElementById('mcb-collections-data');
      if (!this.dataElement) return;

      try {
        this.data = JSON.parse(this.dataElement.textContent);
      } catch (e) {
        console.error('Multi-Collection Bands: Failed to parse collection data', e);
        return;
      }

      this.collections = this.data.collections || [];
      this.settings = this.data.settings || {};
      this.optionTypes = this.data.optionTypes || [];
      
      // Store all variants as individual displayable items
      this.allVariants = [];
      this.filteredVariants = [];
      this.displayedVariants = [];
      
      this.currentPage = 1;
      this.productsPerPage = this.settings.productsPerPage || 28;
      this.isLoadingMore = false;
      this.infiniteScrollObserver = null;
      
      // Dynamic filters - will be populated based on option types found
      this.filters = {
        collection: [],
        color: [], // Unified color filter for 'Color' and 'Metal Color' options
        price_min: this.settings.minPrice / 100,
        price_max: this.settings.maxPrice / 100,
        search: ''
      };
      
      // Initialize empty arrays for each option type filter (except color which is unified)
      this.optionTypes.forEach(optionType => {
        const optionLower = optionType.toLowerCase();
        // Skip color/metal options - they use the unified 'color' filter
        if (optionLower.includes('color') || optionLower.includes('metal')) return;
        
        const handle = this.toHandle(optionType);
        this.filters[handle] = [];
      });
      
      this.sortOrder = 'best-selling';
      
      this.init();
    }

    /**
     * Convert string to handle format
     */
    toHandle(str) {
      return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    }

    init() {
      this.aggregateVariants();
      this.sortOptionFilters();
      this.initInfiniteScroll();
      this.bindEvents();
      this.initPriceSlider();
      this.initFiltersFromCollections();
      this.applyFilters();
      this.hideLoading();
    }

    /**
     * Sort filter options (especially width numerically)
     */
    sortOptionFilters() {
      // Sort width filters numerically
      const widthContainer = this.container.querySelector('.mcb-filter--width .mcb-filter__list');
      if (widthContainer) {
        const items = Array.from(widthContainer.querySelectorAll('.mcb-filter__item'));
        items.sort((a, b) => {
          const valA = parseFloat(a.querySelector('input').value) || 0;
          const valB = parseFloat(b.querySelector('input').value) || 0;
          return valA - valB;
        });
        items.forEach(item => widthContainer.appendChild(item));
      }
    }

    /**
     * Aggregate ALL variants from all collections into a single array
     * Each variant becomes its own displayable item
     */
    aggregateVariants() {
      const variantMap = new Map();
      
      this.collections.forEach(collection => {
        if (collection.products) {
          collection.products.forEach(product => {
            if (product.variants) {
              product.variants.forEach((variant, variantIndex) => {
                // Skip out-of-stock variants - don't show them at all
                if (!variant.available) return;
                
                // Create a unique key for each variant
                const variantKey = `${product.id}-${variant.id}`;
                
                if (!variantMap.has(variantKey)) {
                  // Build option values map for this variant
                  const optionValues = {};
                  product.options.forEach((optionName, index) => {
                    const handle = this.toHandle(optionName);
                    optionValues[handle] = variant.options[index] || '';
                    optionValues[optionName] = variant.options[index] || ''; // Keep original name too
                  });
                  
                  // Extract width from product title if not available as an option
                  // Matches patterns like "2mm", "3mm", "4.5mm", etc.
                  const hasWidthOption = product.options.some(opt => 
                    opt.toLowerCase().includes('width')
                  );
                  
                  if (!hasWidthOption) {
                    const widthMatch = product.title.match(/(\d+(?:\.\d+)?)\s*mm/i);
                    if (widthMatch) {
                      const extractedWidth = widthMatch[1] + 'mm';
                      optionValues['width'] = extractedWidth;
                      optionValues['Width'] = extractedWidth;
                      
                      // Add "Width" to optionTypes if not present
                      if (!this.optionTypes.includes('Width')) {
                        this.optionTypes.push('Width');
                        // Initialize filter array if needed
                        if (!this.filters['width']) {
                          this.filters['width'] = [];
                        }
                      }
                    }
                  }
                  
                  // Create variant display item
                  const variantItem = {
                    id: variant.id,
                    variantId: variant.id,
                    productId: product.id,
                    productHandle: product.handle,
                    productTitle: product.title,
                    variantTitle: variant.title,
                    displayTitle: this.buildVariantDisplayTitle(product.title, variant.title, variant.options),
                    url: `${product.url}?variant=${variant.id}`,
                    price: variant.price,
                    compareAtPrice: variant.compare_at_price || 0,
                    available: variant.available,
                    image: variant.featured_image || product.featured_image,
                    productImage: product.featured_image,
                    vendor: product.vendor,
                    tags: product.tags || [],
                    sku: variant.sku || '',
                    options: variant.options,
                    optionNames: product.options,
                    optionValues: optionValues,
                    collections: [collection.handle],
                    sortPosition: variantIndex
                  };
                  
                  variantMap.set(variantKey, variantItem);
                } else {
                  // Add collection to existing variant
                  const existingVariant = variantMap.get(variantKey);
                  if (!existingVariant.collections.includes(collection.handle)) {
                    existingVariant.collections.push(collection.handle);
                  }
                }
              });
            }
          });
        }
      });
      
      this.allVariants = Array.from(variantMap.values());
      this.filteredVariants = [...this.allVariants];
      
      console.log(`Multi-Collection Bands: Loaded ${this.allVariants.length} variants`);
    }

    /**
     * Build a readable display title for a variant
     */
    buildVariantDisplayTitle(productTitle, variantTitle, options) {
      // If variant title is just "Default Title", use product title
      if (variantTitle === 'Default Title' || variantTitle === productTitle) {
        return productTitle;
      }
      
      // Otherwise, combine product title with variant options
      return `${productTitle} - ${variantTitle}`;
    }

    /**
     * Initialize collection checkboxes as checked
     */
    initFiltersFromCollections() {
      const collectionCheckboxes = this.container.querySelectorAll('input[name="collection"]');
      collectionCheckboxes.forEach(checkbox => {
        if (checkbox.checked) {
          this.filters.collection.push(checkbox.value);
        }
      });
    }

    /**
     * Bind all event listeners
     */
    bindEvents() {
      // Filter toggles (expand/collapse)
      this.container.querySelectorAll('.mcb-filter__header').forEach(header => {
        header.addEventListener('click', (e) => this.handleFilterToggle(e));
      });

      // Checkbox filters
      this.container.querySelectorAll('.mcb-filter__checkbox').forEach(checkbox => {
        checkbox.addEventListener('change', (e) => this.handleCheckboxChange(e));
      });

      // Price inputs
      const priceMinInput = document.getElementById('mcb-price-min');
      const priceMaxInput = document.getElementById('mcb-price-max');
      
      if (priceMinInput) {
        priceMinInput.addEventListener('change', (e) => this.handlePriceInputChange(e, 'min'));
      }
      if (priceMaxInput) {
        priceMaxInput.addEventListener('change', (e) => this.handlePriceInputChange(e, 'max'));
      }

      // Search
      const searchInput = document.getElementById('mcb-search');
      if (searchInput) {
        let searchTimeout;
        searchInput.addEventListener('input', (e) => {
          clearTimeout(searchTimeout);
          searchTimeout = setTimeout(() => {
            this.filters.search = e.target.value.toLowerCase();
            this.applyFilters();
          }, 300);
        });
      }

      // Sort (Desktop)
      const sortSelect = document.getElementById('mcb-sort');
      if (sortSelect) {
        sortSelect.addEventListener('change', (e) => {
          this.sortOrder = e.target.value;
          const mobileSort = document.getElementById('mcb-sort-mobile');
          if (mobileSort) mobileSort.value = e.target.value;
          this.applyFilters();
        });
      }
      
      // Sort (Mobile)
      const mobileSortSelect = document.getElementById('mcb-sort-mobile');
      if (mobileSortSelect) {
        mobileSortSelect.addEventListener('change', (e) => {
          this.sortOrder = e.target.value;
          if (sortSelect) sortSelect.value = e.target.value;
          this.applyFilters();
        });
      }
      
      // Mobile filter toggle
      const mobileFilterToggle = document.getElementById('mcb-filter-toggle-mobile');
      if (mobileFilterToggle) {
        mobileFilterToggle.addEventListener('click', () => this.openFilterDrawer());
      }
      
      // Sticky mobile filter bar observer
      this.initStickyMobileBar();

      // Clear filters buttons
      const clearBtn = document.getElementById('mcb-clear-filters');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => this.clearAllFilters());
      }

      const clearAllBtn = document.getElementById('mcb-clear-all-filters');
      if (clearAllBtn) {
        clearAllBtn.addEventListener('click', () => this.clearAllFilters());
      }

      const resetBtn = document.getElementById('mcb-reset-filters');
      if (resetBtn) {
        resetBtn.addEventListener('click', () => this.clearAllFilters());
      }

      // Initialize infinite scroll
      this.initInfiniteScroll();

      // Mobile filter drawer
      const filterToggle = document.getElementById('mcb-filter-toggle');
      const filterDrawer = document.getElementById('mcb-filter-drawer');
      
      if (filterToggle && filterDrawer) {
        filterToggle.addEventListener('click', () => this.openFilterDrawer());
        
        filterDrawer.querySelectorAll('[data-drawer-close]').forEach(closeBtn => {
          closeBtn.addEventListener('click', () => this.closeFilterDrawer());
        });
      }

      // Clone filters to drawer on init
      this.cloneFiltersToDrawer();
    }

    /**
     * Initialize price range slider
     */
    initPriceSlider(prefix = '') {
      const sliderMin = document.getElementById(`${prefix}mcb-price-slider-min`);
      const sliderMax = document.getElementById(`${prefix}mcb-price-slider-max`);
      const range = document.getElementById(`${prefix}mcb-price-range`);
      const inputMin = document.getElementById(`${prefix}mcb-price-min`);
      const inputMax = document.getElementById(`${prefix}mcb-price-max`);

      if (!sliderMin || !sliderMax || !range) return;

      const updateSlider = () => {
        const minVal = parseInt(sliderMin.value);
        const maxVal = parseInt(sliderMax.value);
        const minPercent = ((minVal - sliderMin.min) / (sliderMin.max - sliderMin.min)) * 100;
        const maxPercent = ((maxVal - sliderMax.min) / (sliderMax.max - sliderMax.min)) * 100;
        
        range.style.left = minPercent + '%';
        range.style.width = (maxPercent - minPercent) + '%';
        
        if (inputMin) inputMin.value = minVal;
        if (inputMax) inputMax.value = maxVal;
      };

      // Use both input and touchmove events for better mobile support
      const handleMinInput = () => {
        if (parseInt(sliderMin.value) > parseInt(sliderMax.value)) {
          sliderMin.value = sliderMax.value;
        }
        updateSlider();
      };
      
      const handleMaxInput = () => {
        if (parseInt(sliderMax.value) < parseInt(sliderMin.value)) {
          sliderMax.value = sliderMin.value;
        }
        updateSlider();
      };

      sliderMin.addEventListener('input', handleMinInput);
      sliderMax.addEventListener('input', handleMaxInput);
      
      // Touch events for better mobile responsiveness
      sliderMin.addEventListener('touchmove', handleMinInput, { passive: true });
      sliderMax.addEventListener('touchmove', handleMaxInput, { passive: true });

      let sliderTimeout;
      const applySliderFilter = () => {
        clearTimeout(sliderTimeout);
        sliderTimeout = setTimeout(() => {
          this.filters.price_min = parseInt(sliderMin.value);
          this.filters.price_max = parseInt(sliderMax.value);
          this.applyFilters();
          this.syncPriceSliders(); // Sync other sliders
        }, 300);
      };

      sliderMin.addEventListener('change', applySliderFilter);
      sliderMax.addEventListener('change', applySliderFilter);
      sliderMin.addEventListener('touchend', applySliderFilter);
      sliderMax.addEventListener('touchend', applySliderFilter);

      updateSlider();
    }

    /**
     * Sync all price sliders (desktop and mobile) to match current filter values
     */
    syncPriceSliders() {
      const updateSet = (prefix) => {
        const sliderMin = document.getElementById(`${prefix}mcb-price-slider-min`);
        const sliderMax = document.getElementById(`${prefix}mcb-price-slider-max`);
        const range = document.getElementById(`${prefix}mcb-price-range`);
        const inputMin = document.getElementById(`${prefix}mcb-price-min`);
        const inputMax = document.getElementById(`${prefix}mcb-price-max`);
        
        if (sliderMin && sliderMax && range) {
          sliderMin.value = this.filters.price_min;
          sliderMax.value = this.filters.price_max;
          
          if (inputMin) inputMin.value = this.filters.price_min;
          if (inputMax) inputMax.value = this.filters.price_max;
          
          const minPercent = ((sliderMin.value - sliderMin.min) / (sliderMin.max - sliderMin.min)) * 100;
          const maxPercent = ((sliderMax.value - sliderMax.min) / (sliderMax.max - sliderMax.min)) * 100;
          
          range.style.left = minPercent + '%';
          range.style.width = (maxPercent - minPercent) + '%';
        }
      };
      
      updateSet('');
      updateSet('drawer-');
    }

    /**
     * Handle filter section toggle
     */
    handleFilterToggle(e) {
      const header = e.currentTarget;
      const isExpanded = header.getAttribute('aria-expanded') === 'true';
      header.setAttribute('aria-expanded', !isExpanded);
    }

    /**
     * Handle checkbox filter change
     */
    handleCheckboxChange(e) {
      const checkbox = e.target;
      const filterType = checkbox.name;
      const value = checkbox.value;

      // Initialize filter array if needed
      if (!this.filters[filterType]) {
        this.filters[filterType] = [];
      }

      if (checkbox.checked) {
        if (!this.filters[filterType].includes(value)) {
          this.filters[filterType].push(value);
        }
      } else {
        this.filters[filterType] = this.filters[filterType].filter(v => v !== value);
      }

      this.applyFilters();
      this.syncDrawerFilters();
    }

    /**
     * Handle price input change
     */
    handlePriceInputChange(e, type) {
      const value = parseInt(e.target.value);
      
      if (type === 'min') {
        this.filters.price_min = value;
      } else {
        this.filters.price_max = value;
      }

      this.applyFilters();
      this.syncPriceSliders();
    }

    /**
     * Apply all active filters to variants
     */
    applyFilters() {
      this.filteredVariants = this.allVariants.filter(variant => {
        // Collection filter
        if (this.filters.collection.length > 0) {
          const inCollection = variant.collections.some(c => this.filters.collection.includes(c));
          if (!inCollection) return false;
        }

        // Price filter
        const price = variant.price / 100;
        if (price < this.filters.price_min || price > this.filters.price_max) {
          return false;
        }

        // Search filter
        if (this.filters.search) {
          const searchTerms = this.filters.search.toLowerCase();
          const searchableText = [
            variant.productTitle,
            variant.variantTitle,
            variant.displayTitle,
            variant.vendor,
            variant.sku,
            ...variant.options,
            ...(variant.tags || [])
          ].join(' ').toLowerCase();
          
          if (!searchableText.includes(searchTerms)) {
            return false;
          }
        }

        // Color filter (special handling - matches ANY color-like option)
        if (this.filters.color && this.filters.color.length > 0) {
          // Find ALL option types that are color-related
          const colorOptionTypes = this.optionTypes.filter(ot => {
            const lower = ot.toLowerCase();
            return lower.includes('color') || lower.includes('metal') || lower.includes('material');
          });
          
          // Check if the variant has a matching value for ANY of these option types
          const matchesColor = colorOptionTypes.some(ot => {
            const handle = this.toHandle(ot);
            const variantValue = variant.optionValues[handle] || variant.optionValues[ot];
            
            if (!variantValue) return false;
            
            const variantColorLower = variantValue.toLowerCase();
            return this.filters.color.some(filterValue => {
              return this.colorMatches(filterValue, variantColorLower);
            });
          });
          
          // If no color option matched, filter out this variant
          if (!matchesColor) return false;
        }

        // Other option-based filters (Width, Finish, Style, Shank, etc.)
        for (const optionType of this.optionTypes) {
          const handle = this.toHandle(optionType);
          const optionLower = optionType.toLowerCase();
          
          // Skip color/metal options - handled above
          if (optionLower.includes('color') || optionLower.includes('metal') || optionLower.includes('material')) continue;
          
          const filterValues = this.filters[handle];
          
          if (filterValues && filterValues.length > 0) {
            // Get the variant's value for this option
            const variantValue = variant.optionValues[handle] || variant.optionValues[optionType];
            
            if (!variantValue) return false;
            
            // Check if variant's value matches any of the selected filter values
            const normalizedVariantValue = this.normalizeValue(variantValue);
            const hasMatch = filterValues.some(filterValue => {
              const normalizedFilterValue = this.normalizeValue(filterValue);
              
              // Direct match
              if (normalizedVariantValue === normalizedFilterValue) return true;
              
              // Numeric match for widths (e.g. "2mm" == "2.0mm")
              if (optionLower.includes('width')) {
                const valNum = parseFloat(normalizedVariantValue);
                const filterNum = parseFloat(normalizedFilterValue);
                if (!isNaN(valNum) && !isNaN(filterNum) && valNum === filterNum) return true;
              }
              
              return false;
            });
            
            if (!hasMatch) return false;
          }
        }

        return true;
      });

      // Sort variants
      this.sortVariants();

      // Reset pagination
      this.currentPage = 1;

      // Update UI
      this.updateVariantCount();
      this.updateActiveFilters();
      this.updateFilterAvailability();
      this.renderVariants();
    }

    /**
     * Update filter options availability based on current selections
     * Greys out and crosses out options that would return no results
     */
    updateFilterAvailability() {
      // Handle color filter separately (unified filter)
      const colorContainer = this.container.querySelector('.mcb-filter[data-filter-type="color"]');
      if (colorContainer) {
        const checkboxes = colorContainer.querySelectorAll('.mcb-filter__checkbox');
        checkboxes.forEach(checkbox => {
          const value = checkbox.value;
          const isCurrentlySelected = this.filters.color && this.filters.color.includes(value);
          
          if (isCurrentlySelected) {
            this.setFilterOptionAvailable(checkbox, true);
            return;
          }
          
          const wouldHaveResults = this.checkOptionAvailability('color', value);
          this.setFilterOptionAvailable(checkbox, wouldHaveResults);
        });
      }
      
      // For each other option type, check which values are still available
      this.optionTypes.forEach(optionType => {
        const optionLower = optionType.toLowerCase();
        // Skip color/metal options - handled above
        if (optionLower.includes('color') || optionLower.includes('metal')) return;
        
        const handle = this.toHandle(optionType);
        const filterContainer = this.container.querySelector(`.mcb-filter[data-filter-type="${handle}"]`);
        if (!filterContainer) return;
        
        const checkboxes = filterContainer.querySelectorAll('.mcb-filter__checkbox');
        
        checkboxes.forEach(checkbox => {
          const value = checkbox.value;
          const isCurrentlySelected = this.filters[handle] && this.filters[handle].includes(value);
          
          // If this option is currently selected, keep it enabled
          if (isCurrentlySelected) {
            this.setFilterOptionAvailable(checkbox, true);
            return;
          }
          
          // Check if selecting this option would return any results
          const wouldHaveResults = this.checkOptionAvailability(handle, value);
          this.setFilterOptionAvailable(checkbox, wouldHaveResults);
        });
      });
      
      // Also update collection filter availability
      const collectionContainer = this.container.querySelector('.mcb-filter[data-filter-type="collection"]');
      if (collectionContainer) {
        const checkboxes = collectionContainer.querySelectorAll('.mcb-filter__checkbox');
        checkboxes.forEach(checkbox => {
          const value = checkbox.value;
          const isCurrentlySelected = this.filters.collection.includes(value);
          
          if (isCurrentlySelected) {
            this.setFilterOptionAvailable(checkbox, true);
            return;
          }
          
          // Check if this collection has any matching variants
          const wouldHaveResults = this.filteredVariants.some(v => v.collections.includes(value)) ||
            this.checkCollectionAvailability(value);
          this.setFilterOptionAvailable(checkbox, wouldHaveResults);
        });
      }
      
      // Sync drawer filters
      this.syncDrawerFilterAvailability();
    }

    /**
     * Check if selecting a specific option value would return results
     * Tests against current filters EXCLUDING the same filter type
     * This way, options within the same filter don't grey each other out
     */
    checkOptionAvailability(optionHandle, optionValue) {
      const normalizedOptionValue = this.normalizeValue(optionValue);
      const isColorFilter = optionHandle === 'color';
      
      // Filter variants by all OTHER filter types (not the one we're checking)
      // This allows multi-select within the same filter type
      return this.allVariants.some(variant => {
        // Check collection filter
        if (this.filters.collection.length > 0) {
          if (!variant.collections.some(c => this.filters.collection.includes(c))) {
            return false;
          }
        }
        
        // Check price filter
        const price = variant.price / 100;
        if (price < this.filters.price_min || price > this.filters.price_max) {
          return false;
        }
        
        // Check search filter
        if (this.filters.search) {
          const searchTerms = this.filters.search.toLowerCase();
          const searchableText = [
            variant.productTitle,
            variant.variantTitle,
            variant.displayTitle,
            variant.vendor,
            variant.sku,
            ...variant.options,
            ...(variant.tags || [])
          ].join(' ').toLowerCase();
          
          if (!searchableText.includes(searchTerms)) {
            return false;
          }
        }
        
        // Check color filter (unless we're checking color availability)
        if (!isColorFilter && this.filters.color && this.filters.color.length > 0) {
          const colorOptionTypes = this.optionTypes.filter(ot => {
            const lower = ot.toLowerCase();
            return lower.includes('color') || lower.includes('metal') || lower.includes('material');
          });
          
          const matchesColor = colorOptionTypes.some(ot => {
            const handle = this.toHandle(ot);
            const variantValue = variant.optionValues[handle] || variant.optionValues[ot];
            
            if (!variantValue) return false;
            
            const variantColorLower = variantValue.toLowerCase();
            return this.filters.color.some(fv => this.colorMatches(fv, variantColorLower));
          });
          
          if (!matchesColor) return false;
        }
        
        // Check OTHER option-based filters (skip the one we're checking and skip color)
        for (const ot of this.optionTypes) {
          const otLower = ot.toLowerCase();
          // Skip color/metal options - handled above
          if (otLower.includes('color') || otLower.includes('metal') || otLower.includes('material')) continue;
          
          const handle = this.toHandle(ot);
          
          // Skip the filter type we're checking availability for
          if (handle === optionHandle) continue;
          
          const filterValues = this.filters[handle];
          if (filterValues && filterValues.length > 0) {
            const variantValue = variant.optionValues[handle] || variant.optionValues[ot];
            if (!variantValue) return false;
            
            const normalizedVariantValue = this.normalizeValue(variantValue);
            const hasMatch = filterValues.some(fv => {
              const normalizedFilterValue = this.normalizeValue(fv);
              if (normalizedVariantValue === normalizedFilterValue) return true;
              
              // Numeric match for widths
              if (otLower.includes('width')) {
                const valNum = parseFloat(normalizedVariantValue);
                const filterNum = parseFloat(normalizedFilterValue);
                if (!isNaN(valNum) && !isNaN(filterNum) && valNum === filterNum) return true;
              }
              return false;
            });
            if (!hasMatch) return false;
          }
        }
        
        // Now check if this variant has the option value we're testing
        if (isColorFilter) {
          const colorOptionTypes = this.optionTypes.filter(ot => {
            const lower = ot.toLowerCase();
            return lower.includes('color') || lower.includes('metal') || lower.includes('material');
          });
          
          // Check if ANY of the color options on this variant matches the value being tested
          return colorOptionTypes.some(ot => {
            const handle = this.toHandle(ot);
            const variantValue = variant.optionValues[handle] || variant.optionValues[ot];
            
            if (!variantValue) return false;
            
            // Use the same color matching logic as the filter
            return this.colorMatches(optionValue, variantValue.toLowerCase());
          });
        } else {
          const optionType = this.optionTypes.find(ot => this.toHandle(ot) === optionHandle);
          if (!optionType) return false;
          
          const variantValue = variant.optionValues[optionHandle] || variant.optionValues[optionType];
          if (!variantValue) return false;
          
          const normalizedVariantValue = this.normalizeValue(variantValue);
          const normalizedOptionValue = this.normalizeValue(optionValue);
          
          if (normalizedVariantValue === normalizedOptionValue) return true;
          
          // Numeric match for widths
          if (optionHandle.includes('width')) {
            const valNum = parseFloat(normalizedVariantValue);
            const optNum = parseFloat(normalizedOptionValue);
            if (!isNaN(valNum) && !isNaN(optNum) && valNum === optNum) return true;
          }
          
          return false;
        }
      });
    }

    /**
     * Check if a collection would have results with current filters
     */
    checkCollectionAvailability(collectionHandle) {
      // Create a temporary filter set with this collection added
      const tempCollectionFilter = [...this.filters.collection, collectionHandle];
      
      return this.allVariants.some(variant => {
        // Check collection
        if (!variant.collections.some(c => tempCollectionFilter.includes(c))) {
          return false;
        }
        
        // Check other active filters
        for (const optionType of this.optionTypes) {
          const handle = this.toHandle(optionType);
          const filterValues = this.filters[handle];
          
          if (filterValues && filterValues.length > 0) {
            const variantValue = variant.optionValues[handle] || variant.optionValues[optionType];
            if (!variantValue) return false;
            
            const normalizedVariantValue = this.normalizeValue(variantValue);
            const hasMatch = filterValues.some(fv => this.normalizeValue(fv) === normalizedVariantValue);
            if (!hasMatch) return false;
          }
        }
        
        // Check price
        const price = variant.price / 100;
        if (price < this.filters.price_min || price > this.filters.price_max) {
          return false;
        }
        
        return true;
      });
    }

    /**
     * Set a filter option as available or unavailable
     */
    setFilterOptionAvailable(checkbox, isAvailable) {
      const label = checkbox.closest('label') || checkbox.closest('.mcb-color-swatch');
      if (!label) return;
      
      if (isAvailable) {
        label.classList.remove('mcb-filter-option--unavailable');
        checkbox.disabled = false;
      } else {
        label.classList.add('mcb-filter-option--unavailable');
        // Don't disable - allow clicking to see "no results" 
        // but visually indicate it's unavailable
      }
    }

    /**
     * Sync filter availability to drawer
     */
    syncDrawerFilterAvailability() {
      const drawerBody = document.getElementById('mcb-drawer-filters');
      if (!drawerBody) return;
      
      // Mirror the availability state from sidebar to drawer
      this.container.querySelectorAll('.mcb-sidebar .mcb-filter__checkbox').forEach(sidebarCheckbox => {
        const name = sidebarCheckbox.name;
        const value = sidebarCheckbox.value;
        const sidebarLabel = sidebarCheckbox.closest('label') || sidebarCheckbox.closest('.mcb-color-swatch');
        if (!sidebarLabel) return;
        
        const isUnavailable = sidebarLabel.classList.contains('mcb-filter-option--unavailable');
        
        const drawerCheckbox = drawerBody.querySelector(`input[name="${name}"][value="${value}"]`);
        if (drawerCheckbox) {
          const drawerLabel = drawerCheckbox.closest('label') || drawerCheckbox.closest('.mcb-color-swatch');
          if (drawerLabel) {
            if (isUnavailable) {
              drawerLabel.classList.add('mcb-filter-option--unavailable');
            } else {
              drawerLabel.classList.remove('mcb-filter-option--unavailable');
            }
          }
        }
      });
    }

    /**
     * Check if a filter color value matches a variant color
     * Maps simplified names (White, Gold, Rose Gold) to all matching variant values
     */
    colorMatches(filterValue, variantColorLower) {
      const filter = filterValue.toLowerCase().trim();
      const variant = variantColorLower.toLowerCase().trim();
      
      // Two-tone filter (contains "/")
      if (filter.includes('/')) {
        // Two-tone filter should only match two-tone variants
        if (!variant.includes('/')) return false;
        
        const filterParts = filter.split('/').map(p => p.trim());
        const variantParts = variant.split('/').map(p => p.trim());
        
        // Check if both parts match
        const firstMatch = this.isColorInFamily(filterParts[0], variantParts[0]);
        const secondMatch = this.isColorInFamily(filterParts[1], variantParts[1]);
        return firstMatch && secondMatch;
      }
      
      // Single color filter - should NOT match two-tone variants
      if (variant.includes('/')) {
        return false;
      }
      
      // Use family-based matching for single colors
      return this.isColorInFamily(filter, variant);
    }
    
    /**
     * Check if variant color belongs to the filter color family
     * White family: white, white gold
     * Gold/Yellow family: gold, yellow, yellow gold
     * Rose/Pink family: pink, rose, rose gold, pink gold
     */
    isColorInFamily(filterColor, variantColor) {
      const filter = filterColor.toLowerCase().trim();
      const variant = variantColor.toLowerCase().trim();
      
      // Define color families
      const whiteFamily = ['white', 'white gold', 'platinum', 'silver'];
      const goldFamily = ['gold', 'yellow', 'yellow gold'];
      const roseFamily = ['pink', 'rose', 'rose gold', 'pink gold'];
      
      // Check which family the filter belongs to
      const filterIsWhite = filter === 'white' || filter === 'white gold' || whiteFamily.some(c => filter.includes(c));
      const filterIsGold = filter === 'gold' || filter === 'yellow' || filter === 'yellow gold' || (goldFamily.some(c => filter.includes(c)) && !filter.includes('white') && !filter.includes('pink') && !filter.includes('rose'));
      const filterIsRose = filter === 'rose gold' || filter === 'rose' || filter === 'pink' || filter === 'pink gold' || roseFamily.some(c => filter.includes(c));
      
      // Check if variant matches the filter's family
      if (filterIsWhite) {
        // White family: exact "white" or contains "white" but not other colors
        return variant === 'white' || 
               (variant.includes('white') && !variant.includes('yellow') && !variant.includes('pink') && !variant.includes('rose'));
      }
      
      if (filterIsGold) {
        // Gold/Yellow family: exact "yellow" or "gold", or contains yellow/gold but not white/pink/rose
        return variant === 'yellow' || 
               variant === 'gold' ||
               ((variant.includes('yellow') || variant.includes('gold')) && 
                !variant.includes('white') && !variant.includes('pink') && !variant.includes('rose'));
      }
      
      if (filterIsRose) {
        // Rose/Pink family: exact "pink" or "rose", or contains pink/rose
        return variant === 'pink' || 
               variant === 'rose' ||
               variant.includes('pink') || 
               variant.includes('rose');
      }
      
      // Fallback: exact match or contains
      return variant === filter || variant.includes(filter) || filter.includes(variant);
    }

    /**
     * Normalize value for comparison
     */
    normalizeValue(value) {
      if (!value) return '';
      return value
        .toLowerCase()
        .replace(/\s*\/\s*/g, '/') // Normalize spaces around slashes
        .replace(/\s+/g, ' ')      // Normalize multiple spaces
        .trim();
    }

    /**
     * Sort variants based on current sort order
     */
    sortVariants() {
      switch (this.sortOrder) {
        case 'title-asc':
          this.filteredVariants.sort((a, b) => a.displayTitle.localeCompare(b.displayTitle));
          break;
        case 'title-desc':
          this.filteredVariants.sort((a, b) => b.displayTitle.localeCompare(a.displayTitle));
          break;
        case 'price-asc':
          this.filteredVariants.sort((a, b) => a.price - b.price);
          break;
        case 'price-desc':
          this.filteredVariants.sort((a, b) => b.price - a.price);
          break;
        case 'created-desc':
          // Keep original order
          break;
        case 'created-asc':
          this.filteredVariants.reverse();
          break;
        case 'best-selling':
        default:
          // Keep original order
          break;
      }
    }

    /**
     * Render variants to the grid
     */
    renderVariants() {
      const grid = document.getElementById('mcb-products-grid');
      const emptyState = document.getElementById('mcb-empty');
      
      if (!grid) return;

      const endIndex = this.currentPage * this.productsPerPage;
      this.displayedVariants = this.filteredVariants.slice(0, endIndex);

      if (this.filteredVariants.length === 0) {
        grid.innerHTML = '';
        if (emptyState) emptyState.style.display = 'flex';
        this.removeInfiniteScrollSentinel();
        return;
      }

      if (emptyState) emptyState.style.display = 'none';

      // If resetting (page 1), replace all content
      if (this.currentPage === 1) {
        grid.innerHTML = this.displayedVariants.map((variant, index) => 
          this.renderVariantCard(variant, index)
        ).join('');
      } else {
        // Otherwise, append new variants
        const startIndex = (this.currentPage - 1) * this.productsPerPage;
        const newVariants = this.filteredVariants.slice(startIndex, endIndex);
        const newHTML = newVariants.map((variant, index) => 
          this.renderVariantCard(variant, startIndex + index)
        ).join('');
        grid.insertAdjacentHTML('beforeend', newHTML);
      }

      // Add or update infinite scroll sentinel
      this.updateInfiniteScrollSentinel();
    }

    /**
     * Render a single variant card
     */
    renderVariantCard(variant, index) {
      const price = this.formatMoney(variant.price);
      const comparePrice = variant.compareAtPrice > variant.price 
        ? this.formatMoney(variant.compareAtPrice) 
        : null;
      
      const isOnSale = variant.compareAtPrice > variant.price;
      const isSoldOut = !variant.available;

      let badge = '';
      if (isSoldOut) {
        badge = '<span class="mcb-product-card__badge mcb-product-card__badge--soldout">Sold Out</span>';
      } else if (isOnSale) {
        badge = '<span class="mcb-product-card__badge mcb-product-card__badge--sale">Sale</span>';
      }

      const image = variant.image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3Crect fill="%23F8F7F2" width="1" height="1"/%3E%3C/svg%3E';

      // Build variant options as the main title (e.g., "White Gold / 6mm / Polished")
      const variantOptionsTitle = variant.options
        .filter(opt => opt && opt !== 'Default Title')
        .join(' / ');
      
      // Use variant options as title, fallback to product title if no options
      const displayTitle = variantOptionsTitle || variant.productTitle;
      
      // Get collection title from the first collection this variant belongs to
      const collectionHandle = variant.collections && variant.collections[0];
      const collectionData = this.collections.find(c => c.handle === collectionHandle);
      const collectionTitle = collectionData ? collectionData.title : '';

      return `
        <article class="mcb-product-card mcb-fade-in" data-variant-id="${variant.variantId}" data-product-id="${variant.productId}" style="animation-delay: ${Math.min(index * 0.03, 0.3)}s">
          <a href="${variant.url}" class="mcb-product-card__link">
            <div class="mcb-product-card__image-wrapper">
              ${badge}
              <img 
                src="${image}" 
                alt="${this.escapeHtml(displayTitle)}" 
                class="mcb-product-card__image"
                loading="lazy"
              >
            </div>
            <div class="mcb-product-card__info">
              <h3 class="mcb-product-card__title">${this.escapeHtml(displayTitle)}</h3>
              ${collectionTitle ? `<p class="mcb-product-card__collection">${this.escapeHtml(collectionTitle)}</p>` : ''}
              <div class="mcb-product-card__price">
                ${comparePrice ? `<span class="mcb-product-card__price-compare">${comparePrice}</span>` : ''}
                <span class="mcb-product-card__price-current">${price}</span>
              </div>
            </div>
          </a>
        </article>
      `;
    }

    /**
     * Build option pills display for variant card
     */
    buildOptionPills(variant) {
      if (!variant.options || variant.options.length === 0) return '';
      
      // Only show pills if the variant has meaningful options (not just "Default Title")
      if (variant.variantTitle === 'Default Title') return '';
      
      const pills = variant.options.map((value, index) => {
        const optionName = variant.optionNames[index] || '';
        const isColor = optionName.toLowerCase().includes('color') || optionName.toLowerCase().includes('metal');
        
        if (isColor) {
          // Show a color swatch
          const swatchStyle = this.getSwatchStyle(value);
          return `<span class="mcb-variant-pill mcb-variant-pill--color" title="${this.escapeHtml(value)}">
            <span class="mcb-variant-pill__swatch${swatchStyle.isTwoTone ? ' mcb-variant-pill__swatch--two-tone' : ''}" style="${swatchStyle.style}"></span>
          </span>`;
        } else {
          // Show text pill
          return `<span class="mcb-variant-pill">${this.escapeHtml(value)}</span>`;
        }
      }).join('');
      
      return `<div class="mcb-product-card__options">${pills}</div>`;
    }

    /**
     * Get swatch style based on color name
     */
    getSwatchStyle(colorName) {
      const colorLower = colorName.toLowerCase();
      
      // Two-tone colors
      if ((colorLower.includes('pink') || colorLower.includes('rose')) && colorLower.includes('white')) {
        if (colorLower.indexOf('pink') < colorLower.indexOf('white') || colorLower.indexOf('rose') < colorLower.indexOf('white')) {
          return { isTwoTone: true, style: '--swatch-color-1: #C9A68A; --swatch-color-2: #E8E8E8;' };
        } else {
          return { isTwoTone: true, style: '--swatch-color-1: #E8E8E8; --swatch-color-2: #C9A68A;' };
        }
      }
      if (colorLower.includes('yellow') && colorLower.includes('white')) {
        if (colorLower.indexOf('yellow') < colorLower.indexOf('white')) {
          return { isTwoTone: true, style: '--swatch-color-1: #D4AF37; --swatch-color-2: #E8E8E8;' };
        } else {
          return { isTwoTone: true, style: '--swatch-color-1: #E8E8E8; --swatch-color-2: #D4AF37;' };
        }
      }
      
      // Single colors
      if (colorLower.includes('pink') || colorLower.includes('rose')) {
        return { isTwoTone: false, style: '--swatch-bg: #C9A68A;' };
      }
      if (colorLower.includes('yellow')) {
        return { isTwoTone: false, style: '--swatch-bg: #D4AF37;' };
      }
      if (colorLower.includes('white')) {
        return { isTwoTone: false, style: '--swatch-bg: #E8E8E8; border-color: #c0c0c0;' };
      }
      if (colorLower.includes('grey') || colorLower.includes('gray') || colorLower.includes('tantalum')) {
        return { isTwoTone: false, style: '--swatch-bg: #707070;' };
      }
      if (colorLower.includes('black')) {
        return { isTwoTone: false, style: '--swatch-bg: #1A1A1A;' };
      }
      if (colorLower.includes('platinum')) {
        return { isTwoTone: false, style: '--swatch-bg: #C0C0C0;' };
      }
      
      return { isTwoTone: false, style: '--swatch-bg: #C9A68A;' };
    }

    /**
     * Initialize infinite scroll observer
     */
    initInfiniteScroll() {
      this.infiniteScrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // Check if there are more variants to load
              const hasMore = this.displayedVariants.length < this.filteredVariants.length;
              if (hasMore && !this.isLoadingMore) {
                this.isLoadingMore = true;
                this.currentPage++;
                this.renderVariants();
                // Reset loading flag after a short delay
                setTimeout(() => {
                  this.isLoadingMore = false;
                }, 300);
              }
            }
          });
        },
        {
          rootMargin: '200px', // Start loading 200px before reaching the sentinel
          threshold: 0.1
        }
      );
    }

    /**
     * Update infinite scroll sentinel element
     */
    updateInfiniteScrollSentinel() {
      const grid = document.getElementById('mcb-products-grid');
      if (!grid) return;

      // Remove existing sentinel
      this.removeInfiniteScrollSentinel();

      // Check if there are more variants to load
      const hasMore = this.displayedVariants.length < this.filteredVariants.length;
      
      if (hasMore) {
        // Create sentinel element
        const sentinel = document.createElement('div');
        sentinel.id = 'mcb-infinite-scroll-sentinel';
        sentinel.className = 'mcb-infinite-scroll-sentinel';
        sentinel.setAttribute('aria-hidden', 'true');
        grid.appendChild(sentinel);
        
        // Observe the sentinel
        if (this.infiniteScrollObserver) {
          this.infiniteScrollObserver.observe(sentinel);
        }
      }
    }

    /**
     * Remove infinite scroll sentinel
     */
    removeInfiniteScrollSentinel() {
      const sentinel = document.getElementById('mcb-infinite-scroll-sentinel');
      if (sentinel) {
        if (this.infiniteScrollObserver) {
          this.infiniteScrollObserver.unobserve(sentinel);
        }
        sentinel.remove();
      }
    }

    /**
     * Update variant count display
     */
    updateVariantCount() {
      const countEl = document.getElementById('mcb-product-count');
      const drawerCountEl = document.getElementById('mcb-drawer-count');
      const mobileCountEl = document.getElementById('mcb-mobile-product-count');
      const mobileFilterCountEl = document.getElementById('mcb-mobile-filter-count');
      
      if (countEl) {
        countEl.textContent = this.filteredVariants.length;
      }
      if (drawerCountEl) {
        drawerCountEl.textContent = this.filteredVariants.length;
      }
      if (mobileCountEl) {
        mobileCountEl.textContent = this.filteredVariants.length;
      }
      
      const filterCount = this.getActiveFilterCount();
      if (mobileFilterCountEl) {
        mobileFilterCountEl.textContent = filterCount > 0 ? filterCount : '';
      }
    }
    
    /**
     * Get count of active filters
     */
    getActiveFilterCount() {
      let count = 0;
      
      // Count color filter
      if (this.filters.color && this.filters.color.length > 0) {
        count += this.filters.color.length;
      }
      
      // Count other option-based filters
      for (const optionType of this.optionTypes) {
        const optionLower = optionType.toLowerCase();
        // Skip color/metal options - counted above
        if (optionLower.includes('color') || optionLower.includes('metal')) continue;
        
        const handle = this.toHandle(optionType);
        if (this.filters[handle] && this.filters[handle].length > 0) {
          count += this.filters[handle].length;
        }
      }
      
      if (this.filters.search) count++;
      
      const defaultMinPrice = this.settings.minPrice / 100;
      const defaultMaxPrice = this.settings.maxPrice / 100;
      if (this.filters.price_min > defaultMinPrice || this.filters.price_max < defaultMaxPrice) count++;
      
      return count;
    }
    
    /**
     * Initialize sticky mobile filter bar
     */
    initStickyMobileBar() {
      const mobileBar = document.getElementById('mcb-mobile-filter-bar');
      if (!mobileBar) return;
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) {
            mobileBar.classList.add('is-sticky');
          } else {
            mobileBar.classList.remove('is-sticky');
          }
        },
        { threshold: [1], rootMargin: '-61px 0px 0px 0px' }
      );
      
      const sentinel = document.createElement('div');
      sentinel.style.height = '1px';
      sentinel.style.marginBottom = '-1px';
      mobileBar.parentNode.insertBefore(sentinel, mobileBar);
      observer.observe(sentinel);
    }

    /**
     * Update active filters display
     */
    updateActiveFilters() {
      const container = document.getElementById('mcb-active-filters');
      const list = document.getElementById('mcb-active-filters-list');
      const badge = document.getElementById('mcb-active-filters-count');
      
      if (!container || !list) return;

      let activeFilters = [];

      // Color filter
      if (this.filters.color && this.filters.color.length > 0) {
        this.filters.color.forEach(value => {
          activeFilters.push({ type: 'color', value, label: `Color: ${value}` });
        });
      }

      // Other option-based filters
      for (const optionType of this.optionTypes) {
        const optionLower = optionType.toLowerCase();
        // Skip color/metal options - handled above
        if (optionLower.includes('color') || optionLower.includes('metal')) continue;
        
        const handle = this.toHandle(optionType);
        const filterValues = this.filters[handle];
        
        if (filterValues && filterValues.length > 0) {
          filterValues.forEach(value => {
            activeFilters.push({ type: handle, value, label: `${optionType}: ${value}` });
          });
        }
      }

      // Price filter (only if modified from defaults)
      const defaultMinPrice = this.settings.minPrice / 100;
      const defaultMaxPrice = this.settings.maxPrice / 100;
      
      if (this.filters.price_min > defaultMinPrice || this.filters.price_max < defaultMaxPrice) {
        activeFilters.push({ 
          type: 'price', 
          value: 'range', 
          label: `$${this.filters.price_min} - $${this.filters.price_max}` 
        });
      }

      // Search filter
      if (this.filters.search) {
        activeFilters.push({ type: 'search', value: this.filters.search, label: `"${this.filters.search}"` });
      }

      if (activeFilters.length === 0) {
        container.style.display = 'none';
        if (badge) badge.textContent = '';
        return;
      }

      container.style.display = 'flex';
      if (badge) badge.textContent = activeFilters.length;

      list.innerHTML = activeFilters.map(filter => `
        <span class="mcb-active-filter-tag">
          ${this.escapeHtml(filter.label)}
          <button type="button" data-remove-filter="${filter.type}" data-filter-value="${this.escapeHtml(filter.value)}" aria-label="Remove filter">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5"/>
            </svg>
          </button>
        </span>
      `).join('');

      // Bind remove filter events
      list.querySelectorAll('[data-remove-filter]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const type = btn.dataset.removeFilter;
          const value = btn.dataset.filterValue;
          this.removeFilter(type, value);
        });
      });
    }

    /**
     * Remove a specific filter
     */
    removeFilter(type, value) {
      if (type === 'price') {
        this.filters.price_min = this.settings.minPrice / 100;
        this.filters.price_max = this.settings.maxPrice / 100;
        
        this.syncPriceSliders();
      } else if (type === 'search') {
        this.filters.search = '';
        const searchInput = document.getElementById('mcb-search');
        if (searchInput) searchInput.value = '';
      } else if (this.filters[type]) {
        this.filters[type] = this.filters[type].filter(v => v !== value);
        
        // Uncheck corresponding checkbox
        const checkbox = this.container.querySelector(`input[name="${type}"][value="${value}"]`);
        if (checkbox) checkbox.checked = false;
      }

      this.applyFilters();
      this.syncDrawerFilters();
    }

    /**
     * Clear all filters
     */
    clearAllFilters() {
      // Reset color filter
      this.filters.color = [];
      
      // Reset all other option-based filters
      for (const optionType of this.optionTypes) {
        const optionLower = optionType.toLowerCase();
        // Skip color/metal options - handled above
        if (optionLower.includes('color') || optionLower.includes('metal')) continue;
        
        const handle = this.toHandle(optionType);
        this.filters[handle] = [];
      }
      
      this.filters.search = '';
      this.filters.price_min = this.settings.minPrice / 100;
      this.filters.price_max = this.settings.maxPrice / 100;

      // Re-check all collection checkboxes
      this.filters.collection = [];
      this.container.querySelectorAll('input[name="collection"]').forEach(checkbox => {
        checkbox.checked = true;
        this.filters.collection.push(checkbox.value);
      });

      // Uncheck all other filter checkboxes and reset unavailable states
      this.container.querySelectorAll('.mcb-filter__checkbox:not([name="collection"])').forEach(checkbox => {
        checkbox.checked = false;
        const label = checkbox.closest('label') || checkbox.closest('.mcb-color-swatch');
        if (label) {
          label.classList.remove('mcb-filter-option--unavailable');
        }
      });

      // Reset price slider
      this.syncPriceSliders();

      // Reset search
      const searchInput = document.getElementById('mcb-search');
      if (searchInput) searchInput.value = '';

      this.applyFilters();
      this.syncDrawerFilters();
    }

    /**
     * Clone filters to mobile drawer
     */
    cloneFiltersToDrawer() {
      const sidebarFilters = document.getElementById('mcb-filters-form');
      const drawerBody = document.getElementById('mcb-drawer-filters');
      
      if (!sidebarFilters || !drawerBody) return;

      const filters = sidebarFilters.querySelectorAll('.mcb-filter');
      filters.forEach(filter => {
        const clone = filter.cloneNode(true);
        
        clone.querySelectorAll('[id]').forEach(el => {
          el.id = 'drawer-' + el.id;
        });
        clone.querySelectorAll('[for]').forEach(el => {
          el.setAttribute('for', 'drawer-' + el.getAttribute('for'));
        });
        
        drawerBody.appendChild(clone);
      });

      drawerBody.querySelectorAll('.mcb-filter__header').forEach(header => {
        header.addEventListener('click', (e) => this.handleFilterToggle(e));
      });

      drawerBody.querySelectorAll('.mcb-filter__checkbox').forEach(checkbox => {
        checkbox.addEventListener('change', (e) => this.handleDrawerCheckboxChange(e));
      });
      
      // Initialize price slider in drawer
      this.initPriceSlider('drawer-');
    }

    /**
     * Handle checkbox change in drawer and sync with sidebar
     */
    handleDrawerCheckboxChange(e) {
      const checkbox = e.target;
      const filterType = checkbox.name;
      const value = checkbox.value;

      const sidebarCheckbox = this.container.querySelector(
        `.mcb-sidebar input[name="${filterType}"][value="${value}"]`
      );
      if (sidebarCheckbox) {
        sidebarCheckbox.checked = checkbox.checked;
      }

      this.handleCheckboxChange(e);
    }

    /**
     * Sync drawer filters with sidebar filters
     */
    syncDrawerFilters() {
      const drawerBody = document.getElementById('mcb-drawer-filters');
      if (!drawerBody) return;

      drawerBody.querySelectorAll('.mcb-filter__checkbox').forEach(drawerCheckbox => {
        const name = drawerCheckbox.name;
        const value = drawerCheckbox.value;
        
        const sidebarCheckbox = this.container.querySelector(
          `.mcb-sidebar input[name="${name}"][value="${value}"]`
        );
        if (sidebarCheckbox) {
          drawerCheckbox.checked = sidebarCheckbox.checked;
        }
      });
    }

    /**
     * Open mobile filter drawer
     */
    openFilterDrawer() {
      const drawer = document.getElementById('mcb-filter-drawer');
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        this.syncDrawerFilters();
      }
    }

    /**
     * Close mobile filter drawer
     */
    closeFilterDrawer() {
      const drawer = document.getElementById('mcb-filter-drawer');
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    }

    /**
     * Hide loading state
     */
    hideLoading() {
      const loading = document.getElementById('mcb-loading');
      if (loading) {
        loading.style.display = 'none';
      }
    }

    /**
     * Format price according to shop money format
     */
    formatMoney(cents) {
      const amount = cents / 100;
      const format = this.settings.moneyFormat || '${{amount}}';
      
      let formatted = format.replace('{{amount}}', amount.toFixed(2));
      formatted = formatted.replace('{{amount_no_decimals}}', Math.round(amount));
      formatted = formatted.replace('{{amount_with_comma_separator}}', amount.toFixed(2).replace('.', ','));
      
      return formatted;
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new MultiCollectionBands());
  } else {
    new MultiCollectionBands();
  }
})();
